import React from 'react';
import { Form, Col, Row, Typography, Input } from 'antd';
import { TicketField, TicketFormResponse } from '@/types';
import { useTranslation } from 'react-i18next';
import { entityFieldList, issueFieldList } from '@/constants';
import { useTicketingStore } from '@/stores';
import { FieldRenderer } from './FieldRenderer';
import Uploader from './Uploader';

const { TextArea } = Input;

interface Props {
  ticketForm?: TicketFormResponse;
  disableEntityId: boolean;
}

const TicketFormBuilder: React.FC<Props> = ({ ticketForm, disableEntityId }) => {
  const { t } = useTranslation();
  const { ticketFormData } = useTicketingStore();
  // A surface may reuse another form's entity field for a different entity
  // (Advance COD batch ticket → PAYMENT form's remittance field). When it
  // supplies an override key, relabel ONLY the entity platform-code field.
  const entityFieldLabelKey = ticketFormData?.entity_field_label_key;

  const checkIsOptionalField = (field: TicketField) => {
    const isDescription = field.title === 'textarea';

    const isIssueField =
      issueFieldList.findIndex((issueFieldId) => issueFieldId === field.id) !== -1;

    return isDescription || isIssueField;
  };

  const renderFormItems = (fields?: TicketField[]) => {
    return (
      <Row gutter={16}>
        {fields?.map((field) => {
          const isFullWidth = field.type === 'textarea';
          const isRequired = field.required;
          const isHiddenField = field.type === 'hidden';

          // 🔴 R10 — THE CATEGORY DROPDOWN IS NOT RENDERED. "As a merchant, I
          // want to raise a ticket without picking a category, so I'm not
          // guessing at a taxonomy I don't control." The merchant was being
          // asked to classify their own problem before describing it, into a
          // vocabulary owned by the ticketing service.
          //
          // ⚠️ SKIPPED AT RENDER, NOT REMOVED FROM THE FORM DEFINITION. The
          // field still arrives from /tickets/forms and is still part of the
          // form the backend knows about; R11 protects reporting that groups
          // by creation-time category. Deleting it from the payload shape would
          // be a bigger change than R10 asks for, and classification is moving
          // to the ticketing service per comment — not to this client.
          if (issueFieldList.includes(field.id)) return null;

          // Render hidden Form.Item for uid field (not visible but included in form values)
          if (isHiddenField) {
            if (field.key === 'uid') {
              return (
                <Form.Item key={field.id} name={String(field.id)} hidden>
                  <Input type='hidden' />
                </Form.Item>
              );
            } else {
              return null;
            }
          }

          const isEntityField = entityFieldList.includes(field.id);
          const fieldLabel =
            isEntityField && entityFieldLabelKey ? t(entityFieldLabelKey) : field.title_in_portal;

          return (
            <Col
              key={field.id}
              className='relative flex flex-col gap-2'
              span={isFullWidth ? 24 : 12}
            >
              {<Typography.Text>{fieldLabel}</Typography.Text>}
              <Form.Item
                name={String(field.id)}
                rules={[
                  {
                    required: checkIsOptionalField(field) ? false : isRequired,
                    message: t('this_field_is_required'),
                  },
                ]}
                initialValue={field.title === 'Priority' ? 'normal' : undefined}
              >
                <FieldRenderer
                  key={field.id}
                  field={field}
                  disabled={entityFieldList.includes(field.id)}
                />
              </Form.Item>
            </Col>
          );
        })}

        {/* Static Description and Attachments - always shown for all forms */}
        <Col span={24} className='relative flex flex-col gap-2'>
          <Typography.Text>{t('description')}</Typography.Text>
          <Form.Item name='description'>
            <TextArea
              rows={4}
              placeholder={t('enter_description')}
              variant='borderless'
              className='rounded-lg border border-solid border-customGrayShade4'
            />
          </Form.Item>
          <Form.Item name='attachments'>
            <Uploader />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  return <>{renderFormItems(ticketForm?.fields)}</>;
};

export default TicketFormBuilder;
